import {
  Component,
  effect,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  signal,
  viewChild,
  DOCUMENT,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime, finalize } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'ngx-sonner';
import { IconComponentV2 } from '../../../../../../shared/components/icon-component/icon.component';
import { ToggleComponent } from '../../../../../../shared/components/ui/toggle/toggle.component';
import {
  DropdownComponent,
  type DropdownOption,
} from '../../../../../../shared/components/ui/dropdown/dropdown.component';
import { MicroServiceHttpService } from '../../../../../../core/services/microServiceHttp.service';
import { ChatbotWorkflowService } from '../../../../../../core/services/chatbot-workflow.service';
import { CommonService } from '../../../../../../core/services/common.service';
import type { providerAPIData, modelAPIData } from '../../../default-settings/default-settings.component';
import {
  WL_ExpressionTypes,
  type NodeScheduleDraft,
  type WorkflowCanvasEdge,
  type WorkflowCanvasNode,
  type WorkflowNodeDraft,
  type WorkflowEdgeTransitionType,
  type WLBotWorkflowNode,
} from './workflow.types';
import { ScheduleAgentService } from '../../../../../../core/services/schedule-agent.service';
import { KnowledgeBaseComponent } from '../knowledge-base/knowledge-base.component';
import {
  canvasToWorkflowGraphSaveRequest,
  cloneAgentCanvasNode,
  emptyCanvasGraph,
  newAgentCanvasNode,
  workflowGraphResponseToCanvas,
} from './workflow-graph.utils';

/** Compact node size (edges use CONNECTOR_Y = center). */
const NODE_W = 180;
const NODE_H = 80;
const CONNECTOR_Y = NODE_H / 2;
/** Default zoom so the graph starts slightly zoomed out. */
const DEFAULT_ZOOM = 0.88;
const CANVAS_WORLD_W = 2400;
const CANVAS_WORLD_H = 1600;
/** Toolbar row above card (pb-2 + button row) so node.x/node.y stay the card top-left for edges. */
const NODE_CHROME_OFFSET_PX = 36;

function isProtectedStartNode(n: WorkflowCanvasNode | null | undefined): boolean {
  return n?.kind === 'START' || n?.workflow?.isStartNode === true;
}

/** Avoid canvas delete shortcuts while typing in the node settings drawer or other text fields. */
function isKeyboardEventFromEditableTarget(ev: KeyboardEvent): boolean {
  const el = ev.target;
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const name = el.nodeName;
  if (name === 'TEXTAREA' || name === 'SELECT') return true;
  if (name !== 'INPUT') return false;
  const type = ((el as HTMLInputElement).type || 'text').toLowerCase();
  const textLike = new Set([
    'text',
    'search',
    'email',
    'password',
    'number',
    'url',
    'tel',
    'date',
    'datetime-local',
    'time',
    'month',
    'week',
  ]);
  return textLike.has(type);
}

/** Settings drawer tabs for agent / workflow nodes (not used for Start). */
export type WorkflowNodeSettingsTabId =
  | 'general'
  | 'knowledge'
  | 'llm'
  | 'tools'
  | 'routing'
  | 'schedule';

@Component({
  selector: 'app-workflow',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponentV2, ToggleComponent, DropdownComponent, KnowledgeBaseComponent],
  templateUrl: './workflow.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './workflow.component.scss',
})
export class WorkflowComponent implements OnInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly chatbotWorkflow = inject(ChatbotWorkflowService);
  private readonly microServiceHttpService = inject(MicroServiceHttpService);
  private readonly commonService = inject(CommonService);
  private readonly scheduleAgentService = inject(ScheduleAgentService);

  private readonly autoSaveSubject = new Subject<void>();
  private autoSaveSub?: Subscription;

  /**
   * JSON snapshot of the last successfully-saved payload.
   * Autosave is skipped when the current serialized payload matches this value,
   * preventing unnecessary API calls when nothing has actually changed.
   */
  private lastSavedPayloadJson: string | null = null;

  constructor() {
    effect(() => {
      const id = this.routingEdgeDetailId();
      if (id == null) return;
      const sel = this.selectedNodeId();
      const edge = this.edges().find((e) => e.id === id);
      if (!sel || !edge || edge.fromNodeId !== sel) {
        this.routingEdgeDetailId.set(null);
      }
    });

    effect(() => {
      const open = this.propertiesPanelOpen();
      this.commonService.updateBodyPanelCount(open ? 1 : -1);
    });

    effect(() => {
      // Reset touched state when selection changes
      this.selectedNodeId();
      this.scheduleTouched.set({});
    }, { allowSignalWrites: true });
  }

  /** Resolved chatbot id from route; when null (new setup), APIs use a dev fallback id. */
  readonly botId = input<number | null>(null);
  readonly botName = input<string | null>(null);
  readonly chatbotKnowledgeBaseId = input<number | null>(null);

  knowledgeBaseComp = viewChild(KnowledgeBaseComponent);

  /** Expands the workflow over the app chrome (sidebar, navbar) with a small viewport inset. */
  readonly fullscreen = signal(false);

  private loadSub: Subscription | null = null;
  private nodeDetailsSub: Subscription | null = null;

  nodes = signal<WorkflowCanvasNode[]>([]);
  edges = signal<WorkflowCanvasEdge[]>([]);

  panX = signal(0);
  panY = signal(0);
  zoom = signal(DEFAULT_ZOOM);

  /** Selection order preserved; use for multi-select (Ctrl+click, marquee). */
  selectedNodeIds = signal<string[]>([]);
  selectedEdgeId = signal<string | null>(null);

  /** Single primary id for properties / patches; null if none or multi-select. */
  readonly selectedNodeId = computed(() => {
    const ids = this.selectedNodeIds();
    return ids.length === 1 ? ids[0]! : null;
  });

  /**
   * Neighbor-path dimming: only when the user turns on “Highlight path” for the current single selection.
   * (Multi-select uses a separate dim rule; never mixed with this.)
   */
  pathHighlightNeighborMode = signal(false);

  readonly connectionDimMode = computed(() => {
    if (!this.pathHighlightNeighborMode()) return null;
    const ids = this.selectedNodeIds();
    if (ids.length !== 1) return null;
    const focusId = ids[0]!;
    const neighborIds = new Set<string>([focusId]);
    for (const e of this.edges()) {
      if (e.fromNodeId === focusId) neighborIds.add(e.toNodeId);
      if (e.toNodeId === focusId) neighborIds.add(e.fromNodeId);
    }
    return { focusId, neighborIds };
  });

  /** Ctrl+drag rectangle selection (world coordinates). */
  marqueeSelecting = signal(false);
  marqueeRectWorld = signal<{ x: number; y: number; w: number; h: number } | null>(null);
  private marqueeStartWorld: { x: number; y: number } | null = null;
  private marqueePointerId: number | null = null;
  /** After marquee, ignore one bubbled click so selection is not cleared. */
  private skipNextCanvasClickClear = false;

  /** True while Ctrl or ⌘ is held — default cursor for multi-select affordance. */
  modMultiSelectKeyDown = signal(false);

  /** Slide-over properties panel (opened only from a node’s settings control). */
  propertiesPanelOpen = signal(false);
  /** While fetching node details for the settings panel. */
  propertiesNodeDetailsLoading = signal(false);

  /** Initial GET workflow graph — blocks canvas until loaded or failed. */
  workflowGraphLoading = signal(true);

  availableTools = signal<any[]>([]);
  loadingTools = signal(false);

  /**
   * While scanning providers to map `modelId` → `providerId` (backend stores model only;
   * provider is client-only for the pickers).
   */
  hydratingWorkflowProvider = signal(false);

  /** Active tab in the node settings drawer (agent/workflow nodes). */
  propertiesSettingsTab = signal<WorkflowNodeSettingsTabId>('general');

  readonly workflowNodeSettingsTabs: { id: WorkflowNodeSettingsTabId; label: string }[] = [
    { id: 'general', label: 'General' },
    { id: 'knowledge', label: 'Knowledge base' },
    { id: 'llm', label: 'AI model' },
    { id: 'tools', label: 'Tools' },
    { id: 'routing', label: 'Sub-agent conditions' },
    { id: 'schedule', label: 'Schedule' },
  ];

  providers = signal<providerAPIData[]>([]);
  providerModels = signal<Record<number, modelAPIData[]>>({});
  modelsLoading = signal<number | null>(null);

  /** Drag connection: source node id (output handle). */
  draggingLinkSourceId = signal<string | null>(null);
  /** Cursor position in world space while dragging a link. */
  linkPreviewEndWorld = signal<{ x: number; y: number } | null>(null);
  /** Input node under cursor while dragging a link (for highlight + snap preview). */
  linkHoverTargetId = signal<string | null>(null);

  private viewportRef = viewChild<ElementRef<HTMLElement>>('viewport');

  draggingNodeId = signal<string | null>(null);
  private dragOffset = { x: 0, y: 0 };

  panning = signal(false);
  private panStart = { x: 0, y: 0, panX: 0, panY: 0 };

  selectedNode = computed(() => {
    const id = this.selectedNodeId();
    if (!id) return null;
    return this.nodes().find((n) => n.id === id) ?? null;
  });

  /**
   * Forward outgoing edges from the selected node, ordered for evaluation (lower sortOrder first).
   */
  readonly outgoingForwardEdges = computed(() => {
    const fromId = this.selectedNodeId();
    if (!fromId) return [];
    const rows: { edge: WorkflowCanvasEdge; targetName: string }[] = [];
    for (const e of this.edges()) {
      if (e.fromNodeId !== fromId) continue;
      const n = this.nodes().find((x) => x.id === e.toNodeId);
      if (!n || n.kind !== 'WORKFLOW' || !n.workflow) continue;
      rows.push({ edge: e, targetName: n.workflow.name ?? 'Agent' });
    }
    rows.sort((a, b) => (a.edge.sortOrder ?? 0) - (b.edge.sortOrder ?? 0));
    return rows;
  });

  /** When set, routing tab shows forward edge detail instead of the list. */
  routingEdgeDetailId = signal<string | null>(null);
  private routingDragEdgeId = signal<string | null>(null);

  readonly routingEdgeDetailContext = computed(() => {
    const eid = this.routingEdgeDetailId();
    const sel = this.selectedNode();
    if (!eid || !sel) return null;
    const edge = this.edges().find((e) => e.id === eid);
    if (!edge || edge.fromNodeId !== sel.id) return null;
    const target = this.getNodeById(edge.toNodeId);
    const sourceName =
      sel.kind === 'START'
        ? (sel.startMeta?.name ?? sel.workflow?.name ?? 'Start')
        : (sel.workflow?.name ?? 'Agent');
    const targetName = target?.workflow?.name ?? 'Agent';
    return { edge, sourceName, targetName };
  });

  /** True when LLM transition is selected and the prompt is empty (validation hint). */
  readonly routingLlmConditionInvalid = computed(() => {
    const ctx = this.routingEdgeDetailContext();
    if (!ctx) return false;
    const t: WorkflowEdgeTransitionType = ctx.edge.transitionType ?? 'ALWAYS';
    if (t !== 'LLM') return false;
    return !((ctx.edge.llmConditionDraft ?? '').trim().length > 0);
  });

  readonly edgeTransitionTypeDropdownOptions: DropdownOption[] = [
    { value: 'ALWAYS', label: 'Unconditional', description: 'This path is always followed when reached.' },
    { value: 'LLM', label: 'LLM condition', description: 'The AI uses your text to decide when this path applies.' },
    { value: 'EXPRESSION', label: 'Expression', description: 'A fixed rule compares a value (for example a field or variable).' },
  ];

  readonly expressionOperatorDropdownOptions: DropdownOption[] = [
    { value: WL_ExpressionTypes.EQUALS, label: 'EQUALS' },
    { value: WL_ExpressionTypes.NOT_EQUALS, label: 'NOT EQUALS' },
    { value: WL_ExpressionTypes.GREATER_THAN, label: 'GREATER THAN' },
    { value: WL_ExpressionTypes.LESS_THAN, label: 'LESS THAN' },
    { value: WL_ExpressionTypes.GREATER_THAN_OR_EQUALS, label: 'GREATER THAN OR EQUALS' },
    { value: WL_ExpressionTypes.LESS_THAN_OR_EQUALS, label: 'LESS THAN OR EQUALS' },
    { value: WL_ExpressionTypes.AND, label: 'AND' },
    { value: WL_ExpressionTypes.OR, label: 'OR' },
  ];

  /** Validation errors for the selected node's schedule draft. */
  readonly scheduleErrors = computed(() => {
    const sel = this.selectedNode();
    const draft = sel?.workflow?.scheduleDraft;
    if (!draft) return { name: null, cron: null, action: null };

    const hasName = !!draft.name?.trim();
    const hasCron = !!draft.cronExpr?.trim();
    const hasAction = !!draft.triggerPrompt?.trim();
    const anyFilled = hasName || hasCron || hasAction;

    // If nothing filled and no PID (new schedule), no errors (can walk away)
    if (!anyFilled && !draft.pid) return { name: null, cron: null, action: null };

    return {
      name: !hasName ? 'Schedule name is required' : null,
      cron: !hasCron
        ? 'Cron expression is required'
        : !this.isValidScheduleCronExpression(draft.cronExpr)
          ? 'Invalid cron format (5-field or 6-field with seconds)'
          : null,
      action: !hasAction ? 'Schedule action is required' : null,
    };
  });

  /**
   * Validates the schedule cron expression shape used by the backend cron parser.
   * @param cronExpr - Cron expression entered in the schedule form.
   * @returns True when the expression has 5 fields or an optional leading seconds field.
   */
  private isValidScheduleCronExpression(cronExpr: string): boolean {
    const fields = cronExpr.trim().split(/\s+/).filter(Boolean);
    if (fields.length !== 5 && fields.length !== 6) return false;

    const fieldPattern =
      /^(?:\*|\d+|\*\/\d+|\d+-\d+|\d+(?:,\d+)+|\d+-\d+\/\d+)$/;

    return fields.every((field) => fieldPattern.test(field));
  }

  /** Fields that have been interacted with in the current selection. */
  readonly scheduleTouched = signal<Record<string, boolean>>({});

  markScheduleTouched(field: string): void {
    this.scheduleTouched.update((s) => ({ ...s, [field]: true }));
  }

  canClosePropertiesPanel(): boolean {
    if (!this.propertiesPanelOpen()) return true;
    const errs = this.scheduleErrors();
    if (errs.name || errs.cron || errs.action) {
      const msg = errs.name || errs.cron || errs.action;
      toast.error(`Please complete the schedule: ${msg}`);
      this.selectPropertiesTab('schedule');
      return false;
    }
    return true;
  }

  readonly upstreamNodeOptions = computed((): DropdownOption[] => {
    const selId = this.selectedNodeId();
    // Show all AGENT nodes in this chatbot except the current one
    return this.nodes()
      .filter((n) => n.kind === 'WORKFLOW' && n.id !== selId && n.workflow?.pid)
      .map((n) => ({
        value: String(n.workflow!.pid),
        label: n.workflow!.name || 'Unnamed Agent',
      }));
  });

  readonly upstreamNodeSelectedValue = computed(() => {
    const ids = this.selectedNode()?.workflow?.scheduleDraft?.upstreamWorkflowIds;
    return ids ? ids.map(id => String(id)) : [];
  });

  readonly workflowNodeProviderDropdownOptions = computed((): DropdownOption[] => {
    const none: DropdownOption = {
      value: '',
      label: 'Use chatbot default',
      description: 'Uses the same provider as the main agent settings.',
    };
    return [
      none,
      ...this.providers().map((p) => ({
        value: String(p.pid),
        label: p.name,
      })),
    ];
  });

  readonly workflowNodeModelDropdownOptions = computed((): DropdownOption[] => {
    const none: DropdownOption = {
      value: '',
      label: 'Use chatbot default',
      description: 'Uses the same model as the main agent settings.',
    };
    const sel = this.selectedNode();
    const pid = sel?.workflow?.providerId;
    if (pid == null) return [none];
    const models = this.providerModels()[pid] ?? [];
    return [
      none,
      ...models.map((m) => ({
        value: String(m.pid),
        label: m.name || m.model,
      })),
    ];
  });

  readonly workflowNodeProviderSelectedValue = computed(() => {
    const id = this.selectedNode()?.workflow?.providerId;
    return id != null ? String(id) : '';
  });

  readonly workflowNodeModelSelectedValue = computed(() => {
    const id = this.selectedNode()?.workflow?.modelId;
    return id != null && id !== undefined ? String(id) : '';
  });

  /** Pan + zoom as one transform; translate3d (no will-change) avoids blurry upscaled layers when zoomed in. */
  readonly worldTransformStyle = computed(() => {
    const x = this.panX();
    const y = this.panY();
    const z = this.zoom();
    return `translate3d(${x}px, ${y}px, 0) scale(${z})`;
  });

  worldWidth = CANVAS_WORLD_W;
  worldHeight = CANVAS_WORLD_H;

  private effectiveChatbotId(): number {
    return this.botId() ?? 1;
  }

  ngOnInit(): void {
    this.getProvidersApi();
    const cid = this.effectiveChatbotId();
    this.loadToolsApi(cid);
    this.loadSub?.unsubscribe();
    this.workflowGraphLoading.set(true);
    this.loadSub = this.chatbotWorkflow
      .getWorkflowGraph(cid)
      .pipe(finalize(() => this.workflowGraphLoading.set(false)))
      .subscribe({
        next: (graph) => {
          const g = workflowGraphResponseToCanvas(graph, this.nodes());
          this.nodes.set(g.nodes);
          this.edges.set(g.edges);
          // Seed the dirty-check snapshot so the first autosave after load
          // is a no-op when nothing has changed.
          queueMicrotask(() => {
            this.hydrateAllWorkflowNodesMissingProvider();
            this.lastSavedPayloadJson = this.serializeCurrentGraph();
          });
        },
        error: () => {
          const g = emptyCanvasGraph(cid);
          this.nodes.set(g.nodes);
          this.edges.set(g.edges);
        },
      });

    this.autoSaveSub?.unsubscribe();
    this.autoSaveSub = this.autoSaveSubject
      .pipe(debounceTime(1000))
      .subscribe(() => {
        this.saveWorkflow(undefined, true);
      });
  }

  saveWorkflow(ev?: Event, isAutoSave = false): void {
    ev?.stopPropagation();

    // 1. Validate Node Details
    for (const n of this.nodes()) {
      if (n.kind === 'WORKFLOW') {
        const name = n.workflow?.name?.trim() || '';
        if (!name) {
          if (!isAutoSave) {
            toast.error('Agent name cannot be empty.');
            this.selectSingleNode(n.id);
            this.openNodeProperties(n.id, undefined, true);
            this.selectPropertiesTab('general');
          }
          return;
        }
        // if (name.toLowerCase() === 'new agent') {
        //   toast.error('Please provide a specific name for your agent instead of "New agent".');
        //   this.selectSingleNode(n.id);
        //   this.openNodeProperties(n.id, undefined, true);
        //   this.selectPropertiesTab('general');
        //   return;
        // }
        // if (!n.workflow?.systemPrompt?.trim()) {
        //   toast.error(`Please provide instructions for agent "${name}" in the General tab.`);
        //   this.selectSingleNode(n.id);
        //   this.openNodeProperties(n.id, undefined, true);
        //   this.selectPropertiesTab('general');
        //   return;
        // }
      } else if (n.kind === 'START') {
        if (!n.startMeta?.name?.trim()) {
          if (!isAutoSave) {
            toast.error('Start node name cannot be empty.');
            this.selectSingleNode(n.id);
            this.openNodeProperties(n.id, undefined, true);
            // this.selectPropertiesTab('general');
          }
          return;
        }
      }
    }

    // 2. Validate Schedule Details (if tab is open or node has a draft)
    const errs = this.scheduleErrors();
    if (errs.name || errs.cron || errs.action) {
      if (!isAutoSave) {
        const firstErr = errs.name || errs.cron || errs.action;
        toast.error(firstErr!);
        this.selectPropertiesTab('schedule');
      }
      return;
    }


    const cid = this.effectiveChatbotId();
    try {
      if (isAutoSave && !this.hasGraphChanged()) {
        return;
      }

      const payload = canvasToWorkflowGraphSaveRequest(this.nodes(), this.edges());

      this.chatbotWorkflow.updateWorkflowGraph(cid, payload, { showLoader: !isAutoSave }).subscribe({
        next: (graph) => {
          this.lastSavedPayloadJson = JSON.stringify(payload);
          if (!isAutoSave) {
            toast.success('Workflow saved');
          }
          const g = workflowGraphResponseToCanvas(graph, this.nodes());
          this.nodes.set(g.nodes);
          this.edges.set(g.edges);
          queueMicrotask(() => this.hydrateAllWorkflowNodesMissingProvider());
        },
        error: () => {
          if (!isAutoSave) {
            toast.error('Failed to save workflow');
          } else {
            // Clear snapshot so the next autosave retries effectively.
            this.lastSavedPayloadJson = null;
          }
        },
      });
    } catch {
      if (!isAutoSave) {
        toast.error('Failed to prepare workflow for saving');
      }
    }
  }
  

  /**
   * Serializes the current nodes and edges to a JSON string for comparison.
   */
  private serializeCurrentGraph(): string {
    try {
      return JSON.stringify(canvasToWorkflowGraphSaveRequest(this.nodes(), this.edges()));
    } catch {
      return '';
    }
  }

  /**
   * Compares the current canvas state with the last successfully saved snapshot.
   * Returns true if there are meaningful differences.
   */
  private hasGraphChanged(): boolean {
    const current = this.serializeCurrentGraph();
    return current !== this.lastSavedPayloadJson;
  }

  @HostListener('document:pointerup', ['$event'])
  onDocumentPointerUp(ev: PointerEvent): void {
    if (this.draggingNodeId()) {
      this.draggingNodeId.set(null);
      this.autoSaveSubject.next();
    }
    if (this.draggingLinkSourceId()) {
      this.completeLinkDrag(ev);
    }
    if (this.marqueeSelecting()) {
      this.finishMarquee(ev);
    }
    this.panning.set(false);
  }

  @HostListener('document:pointercancel')
  onDocumentPointerCancel(): void {
    if (this.draggingLinkSourceId()) {
      this.cancelLinkDrag();
    }
    if (this.marqueeSelecting()) {
      this.cancelMarquee();
    }
  }

  @HostListener('document:pointermove', ['$event'])
  onDocumentPointerMove(ev: PointerEvent): void {
    const mod = ev.ctrlKey || ev.metaKey;
    if (mod !== this.modMultiSelectKeyDown()) {
      this.modMultiSelectKeyDown.set(mod);
    }
    if (this.draggingLinkSourceId()) {
      this.updateLinkDrag(ev);
      return;
    }
    if (this.marqueeSelecting()) {
      this.updateMarquee(ev);
      return;
    }
    const draggingId = this.draggingNodeId();
    if (draggingId) {
      const vp = this.viewportRef()?.nativeElement;
      if (!vp) return;
      const r = vp.getBoundingClientRect();
      const lx = ev.clientX - r.left;
      const ly = ev.clientY - r.top;
      const z = this.zoom();
      const nx = (lx - this.dragOffset.x - this.panX()) / z;
      const ny = (ly - this.dragOffset.y - this.panY()) / z;
      this.nodes.update((list) =>
        list.map((n) =>
          n.id === draggingId ? { ...n, x: Math.round(nx), y: Math.round(ny) } : n
        )
      );
      return;
    }
    if (this.panning()) {
      this.panX.set(this.panStart.panX + (ev.clientX - this.panStart.x));
      this.panY.set(this.panStart.panY + (ev.clientY - this.panStart.y));
    }
  }

  @HostListener('window:blur')
  onWindowBlur(): void {
    this.modMultiSelectKeyDown.set(false);
  }

  @HostListener('window:keyup', ['$event'])
  onWindowKeyup(ev: KeyboardEvent): void {
    if (ev.key === 'Control' || ev.key === 'Meta') {
      this.modMultiSelectKeyDown.set(false);
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'Control' || ev.key === 'Meta') {
      this.modMultiSelectKeyDown.set(true);
    }
    if (ev.key === 'Escape') {
      if (this.propertiesPanelOpen()) {
        if (!this.canClosePropertiesPanel()) return;
        this.closePropertiesPanel();
        return;
      }
      if (this.fullscreen()) {
        this.setFullscreen(false);
        return;
      }
      this.cancelLinkDrag();
      this.selectedEdgeId.set(null);
      this.selectedNodeIds.set([]);
      this.pathHighlightNeighborMode.set(false);
      return;
    }
    if (ev.key === 'Delete' || ev.key === 'Backspace') {
      const t = ev.target as HTMLElement | null;
      if (t?.closest?.('[data-wf-properties-panel]')) return;
      if (isKeyboardEventFromEditableTarget(ev)) return;
      const edgeId = this.selectedEdgeId();
      if (edgeId) {
        this.edges.update((e) => e.filter((x) => x.id !== edgeId));
        this.selectedEdgeId.set(null);
        ev.preventDefault();
        return;
      }
      const ids = this.selectedNodeIds().filter(
        (id) => !isProtectedStartNode(this.getNodeById(id))
      );
      if (ids.length > 0) {
        this.deleteNodes(ids);
        ev.preventDefault();
      }
    }
  }

  onWheel(ev: WheelEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    if (ev.ctrlKey || ev.metaKey) {
      const delta = -ev.deltaY * 0.001;
      this.zoom.update((z) => Math.min(1.55, Math.max(0.35, z + delta)));
    } else {
      this.panX.update((x) => x - ev.deltaX);
      this.panY.update((y) => y - ev.deltaY);
    }
  }

  startPan(ev: PointerEvent): void {
    const t = ev.target as HTMLElement;
    if (t.closest('[data-node-card]')) return;
    if (t.closest('[data-wf-node-toolbar]')) return;
    if (t.closest('[data-workflow-edge]')) return;
    if (t.closest('[data-wf-output-handle]')) return;
    if (t.closest('[data-wf-input-handle]')) return;
    if (this.draggingLinkSourceId()) return;
    if (ev.ctrlKey || ev.metaKey) {
      this.beginMarquee(ev);
      return;
    }
    this.panning.set(true);
    this.panStart = { x: ev.clientX, y: ev.clientY, panX: this.panX(), panY: this.panY() };
    (ev.target as HTMLElement).setPointerCapture?.(ev.pointerId);
  }

  /** Plain click: single selection. Does not open the properties panel. Clears path highlight. */
  selectSingleNode(id: string, ev?: Event): void {
    // If the panel is open and we're switching nodes, check if the current one is valid
    if (this.propertiesPanelOpen() && !this.isNodeSelected(id)) {
      if (!this.canClosePropertiesPanel()) return;
    }

    ev?.stopPropagation();
    this.pathHighlightNeighborMode.set(false);
    this.selectedNodeIds.set([id]);
    this.selectedEdgeId.set(null);
  }

  toggleNodeSelection(id: string, ev?: Event): void {
    if (this.propertiesPanelOpen() && !this.isNodeSelected(id)) {
      if (!this.canClosePropertiesPanel()) return;
    }

    ev?.stopPropagation();
    this.pathHighlightNeighborMode.set(false);
    this.selectedEdgeId.set(null);
    this.selectedNodeIds.update((list) => {
      const i = list.indexOf(id);
      if (i >= 0) {
        return list.filter((x) => x !== id);
      }
      return [...list, id];
    });
    if (this.selectedNodeIds().length !== 1) {
      if (this.propertiesPanelOpen()) {
        if (!this.canClosePropertiesPanel()) return;
        this.closePropertiesPanel();
      }
    }
  }

  isNodeSelected(id: string): boolean {
    return this.selectedNodeIds().includes(id);
  }

  isNodeDimmed(id: string): boolean {
    const ids = this.selectedNodeIds();
    if (ids.length >= 2) {
      return !ids.includes(id);
    }
    const m = this.connectionDimMode();
    if (!m) return false;
    return !m.neighborIds.has(id);
  }

  isEdgeDimmed(edge: WorkflowCanvasEdge): boolean {
    const ids = this.selectedNodeIds();
    if (ids.length >= 2) {
      const set = new Set(ids);
      return !(set.has(edge.fromNodeId) && set.has(edge.toNodeId));
    }
    const m = this.connectionDimMode();
    if (!m) return false;
    return edge.fromNodeId !== m.focusId && edge.toNodeId !== m.focusId;
  }

  /** Toggle neighbor-path dim for this node; assigns single selection when needed. */
  togglePathHighlight(nodeId: string, ev: Event): void {
    ev.stopPropagation();
    ev.preventDefault();
    const ids = this.selectedNodeIds();
    if (
      ids.length === 1 &&
      ids[0] === nodeId &&
      this.pathHighlightNeighborMode()
    ) {
      this.pathHighlightNeighborMode.set(false);
      return;
    }
    if (ids.length !== 1 || ids[0] !== nodeId) {
      this.selectedNodeIds.set([nodeId]);
      this.selectedEdgeId.set(null);
      this.propertiesPanelOpen.set(false);
    }
    this.pathHighlightNeighborMode.set(true);
  }

  isPathHighlightActiveForNode(nodeId: string): boolean {
    return (
      this.pathHighlightNeighborMode() &&
      this.selectedNodeIds().length === 1 &&
      this.selectedNodeIds()[0] === nodeId
    );
  }

  private beginMarquee(ev: PointerEvent): void {
    const w = this.screenToWorld(ev.clientX, ev.clientY);
    if (!w) return;
    this.marqueeSelecting.set(true);
    this.marqueeStartWorld = { x: w.x, y: w.y };
    this.marqueeRectWorld.set({ x: w.x, y: w.y, w: 0, h: 0 });
    this.marqueePointerId = ev.pointerId;
    const vp = this.viewportRef()?.nativeElement;
    vp?.setPointerCapture?.(ev.pointerId);
    ev.preventDefault();
    ev.stopPropagation();
  }

  private updateMarquee(ev: PointerEvent): void {
    const start = this.marqueeStartWorld;
    if (!start) return;
    const cur = this.screenToWorld(ev.clientX, ev.clientY);
    if (!cur) return;
    const x1 = Math.min(start.x, cur.x);
    const y1 = Math.min(start.y, cur.y);
    const x2 = Math.max(start.x, cur.x);
    const y2 = Math.max(start.y, cur.y);
    this.marqueeRectWorld.set({ x: x1, y: y1, w: x2 - x1, h: y2 - y1 });
  }

  private finishMarquee(ev: PointerEvent): void {
    const vp = this.viewportRef()?.nativeElement;
    if (vp != null && this.marqueePointerId != null) {
      try {
        vp.releasePointerCapture(this.marqueePointerId);
      } catch {
        /* already released */
      }
    }
    this.marqueePointerId = null;
    this.marqueeSelecting.set(false);
    this.marqueeStartWorld = null;

    const rect = this.marqueeRectWorld();
    this.marqueeRectWorld.set(null);
    this.skipNextCanvasClickClear = true;

    if (rect && rect.w >= 4 && rect.h >= 4) {
      const selected = this.nodes()
        .filter((n) => this.nodeIntersectsMarqueeRect(n, rect))
        .map((n) => n.id);
      this.pathHighlightNeighborMode.set(false);
      this.selectedNodeIds.set(selected);
      this.selectedEdgeId.set(null);
      if (selected.length !== 1) {
        this.propertiesPanelOpen.set(false);
      }
    }
  }

  private cancelMarquee(): void {
    const vp = this.viewportRef()?.nativeElement;
    if (vp != null && this.marqueePointerId != null) {
      try {
        vp.releasePointerCapture(this.marqueePointerId);
      } catch {
        /* ignore */
      }
    }
    this.marqueePointerId = null;
    this.marqueeSelecting.set(false);
    this.marqueeStartWorld = null;
    this.marqueeRectWorld.set(null);
  }

  private nodeIntersectsMarqueeRect(
    n: WorkflowCanvasNode,
    r: { x: number; y: number; w: number; h: number }
  ): boolean {
    const x = n.x;
    const y = n.y - NODE_CHROME_OFFSET_PX;
    const w = NODE_W;
    const h = NODE_CHROME_OFFSET_PX + NODE_H;
    return !(x + w < r.x || r.x + r.w < x || y + h < r.y || r.y + r.h < y);
  }

  openNodeProperties(nodeId: string, ev?: Event, skipFetch = false): void {
    ev?.stopPropagation();
    ev?.preventDefault();
    this.pathHighlightNeighborMode.set(false);
    this.selectedNodeIds.set([nodeId]);
    this.selectedEdgeId.set(null);
    this.cancelLinkDrag();
    this.routingEdgeDetailId.set(null);
    this.propertiesPanelOpen.set(true);
    this.propertiesNodeDetailsLoading.set(false);

    this.nodeDetailsSub?.unsubscribe();
    this.nodeDetailsSub = null;

    const node = this.getNodeById(nodeId);
    const wfPid = node?.workflow?.pid;

    if (isProtectedStartNode(node)) {
      this.propertiesSettingsTab.set('routing');
      return;
    }

    if (wfPid == null || skipFetch) {
      return;
    }

    this.propertiesSettingsTab.set('general');
    const pid = wfPid;
    this.propertiesNodeDetailsLoading.set(true);
    const cid = this.effectiveChatbotId();
    this.nodeDetailsSub = this.chatbotWorkflow.getWorkflowNodeDetails(cid, pid).subscribe({
      next: (patch) => {
        this.propertiesNodeDetailsLoading.set(false);
        this.nodes.update((list) =>
          list.map((n) =>
            n.id === nodeId && n.workflow
              ? { ...n, workflow: { ...n.workflow, ...patch } }
              : n
          )
        );
        queueMicrotask(() => this.hydrateAllWorkflowNodesMissingProvider());
      },
      error: () => this.propertiesNodeDetailsLoading.set(false),
    });
  }

  selectPropertiesTab(tab: WorkflowNodeSettingsTabId): void {
    this.propertiesSettingsTab.set(tab);
    if (tab !== 'routing') {
      this.routingEdgeDetailId.set(null);
    }
    if (tab === 'llm') {
      queueMicrotask(() => this.hydrateAllWorkflowNodesMissingProvider());
      const pid = this.selectedNode()?.workflow?.providerId;
      if (pid != null) {
        this.getProviderModelsApi(pid);
      }
    }
    if (tab === 'schedule') {
      this.loadNodeScheduleIfNeeded();
    }
  }

  private loadNodeScheduleIfNeeded(): void {
    const sel = this.selectedNode();
    const nodePid = sel?.workflow?.pid;
    const botId = this.botId();
    // Already loaded or node not persisted yet
    if (!nodePid || !botId || sel?.workflow?.scheduleDraft !== undefined) return;
    this.scheduleAgentService.getSchedules(botId).subscribe({
      next: (res) => {
        const match = (res.data ?? []).find((s) => s.nodeId === nodePid) ?? null;
        const nodeId = sel.id;
        this.nodes.update((list) =>
          list.map((n) => {
            if (n.id !== nodeId || !n.workflow) return n;
            const draft = match
              ? ({
                  pid: match.pid,
                  name: match.name,
                  triggerPrompt: match.triggerPrompt,
                  cronExpr: match.cronExpr,
                  timezone: match.timezone,
                  isActive: match.isActive,
                  maxRetries: match.maxRetries,
                  retryIntervalMins: match.retryIntervalMins,
                  upstreamWorkflowIds: match.upstreamWorkflowIds || [],
                } satisfies NodeScheduleDraft)
              : null;
            return { ...n, workflow: { ...n.workflow, scheduleDraft: draft } };
          })
        );
      },
    });
  }

  getProvidersApi(): void {
    this.microServiceHttpService.get('whitelabel', 'default-config/llm-providers', {
      showToast: false,
    }).subscribe({
      next: (response: any) => {
        this.providers.set((response.data ?? []) as providerAPIData[]);
        queueMicrotask(() => this.hydrateAllWorkflowNodesMissingProvider());
      },
      error: (err: unknown) => {
        console.error(err);
      },
    });
  }

  /** Uses cached `providerModels` maps: which provider lists this model id. */
  private findProviderIdForModelId(modelId: number): number | null {
    for (const [key, models] of Object.entries(this.providerModels())) {
      const providerId = Number(key);
      if (Number.isNaN(providerId)) continue;
      if (models.some((m) => m.pid === modelId)) return providerId;
    }
    return null;
  }

  /**
   * Fills `workflow.providerId` when the node has `modelId` from the API but no provider
   * (provider is UI-only). Loads each resolved provider’s model list for dropdowns.
   */
  private hydrateAllWorkflowNodesMissingProvider(): void {
    const tryResolveFromCache = (): void => {
      for (const n of this.nodes()) {
        if (n.kind !== 'WORKFLOW' || !n.workflow) continue;
        const w = n.workflow;
        if (w.modelId == null || w.providerId != null) continue;
        const providerId = this.findProviderIdForModelId(w.modelId);
        if (providerId != null) {
          this.patchTargetWorkflowNode(n.id, { providerId });
          this.getProviderModelsApi(providerId);
        }
      }
    };
    tryResolveFromCache();

    const provers = this.providers();
    if (provers.length === 0) return;

    let pIdx = 0;
    const tryNextProvider = (): void => {
      const anyMissing = this.nodes().some(
        (n) =>
          n.kind === 'WORKFLOW' &&
          n.workflow?.modelId != null &&
          n.workflow.providerId == null,
      );
      if (!anyMissing) {
        this.hydratingWorkflowProvider.set(false);
        return;
      }
      if (pIdx >= provers.length) {
        this.hydratingWorkflowProvider.set(false);
        return;
      }
      this.hydratingWorkflowProvider.set(true);
      const p = provers[pIdx++]!.pid;
      this.getProviderModelsApi(p, {
        afterLoad: () => {
          tryResolveFromCache();
          queueMicrotask(() => tryNextProvider());
        },
      });
    };

    if (
      this.nodes().some(
        (n) =>
          n.kind === 'WORKFLOW' &&
          n.workflow?.modelId != null &&
          n.workflow.providerId == null,
      )
    ) {
      tryNextProvider();
    }
  }

  getProviderModelsApi(
    providerId: number,
    opts?: { afterLoad?: (models: modelAPIData[]) => void },
  ): void {
    const cached = this.providerModels()[providerId];
    if (cached !== undefined) {
      opts?.afterLoad?.(cached);
      return;
    }
    this.modelsLoading.set(providerId);
    this.microServiceHttpService.get('whitelabel', `default-config/llm-models/${providerId}`, {
      showToast: false,
      showLoader: false,
    }).subscribe({
      next: (response: any) => {
        const list = (response.data ?? []) as modelAPIData[];
        this.providerModels.update((map) => ({ ...map, [providerId]: list }));
        this.modelsLoading.set(null);
        opts?.afterLoad?.(list);
      },
      error: (err: unknown) => {
        console.error(err);
        this.modelsLoading.set(null);
      },
    });
  }

  onUpstreamNodeChange(values: unknown): void {
    const ids = Array.isArray(values)
      ? values.map(v => Number(v)).filter(n => !isNaN(n) && n > 0)
      : [];
    this.patchNodeSchedule({ upstreamWorkflowIds: ids });
  }

  onWorkflowNodeProviderChange(value: unknown): void {
    const s = value === null || value === undefined ? '' : String(value);
    if (s === '') {
      this.patchSelectedWorkflow({ providerId: null, modelId: undefined });
      return;
    }
    const providerId = Number(s);
    this.patchSelectedWorkflow({ providerId, modelId: undefined });
    this.getProviderModelsApi(providerId);
  }

  onWorkflowNodeModelChange(value: unknown): void {
    const s = value === null || value === undefined ? '' : String(value);
    if (s === '') {
      this.patchSelectedWorkflow({ modelId: undefined });
      return;
    }
    const modelId = Number(s);
    const pid = this.selectedNode()?.workflow?.providerId;
    if (pid == null) return;
    const models = this.providerModels()[pid] ?? [];
    const m = models.find((x) => x.pid === modelId);
    if (!m) return;
    this.patchSelectedWorkflow({ modelId: m.pid });
  }

  patchTargetWorkflowNode(targetNodeId: string, patch: Partial<WLBotWorkflowNode>): void {
    this.nodes.update((list) =>
      list.map((n) => {
        if (n.id !== targetNodeId || !n.workflow) return n;
        return { ...n, workflow: { ...n.workflow, ...patch } };
      })
    );
  }

  patchEdge(edgeId: string, patch: Partial<WorkflowCanvasEdge>): void {
    this.edges.update((list) => list.map((e) => (e.id === edgeId ? { ...e, ...patch } : e)));
  }

  openRoutingEdgeDetail(edgeId: string): void {
    this.routingEdgeDetailId.set(edgeId);
  }

  closeRoutingEdgeDetail(): void {
    this.routingEdgeDetailId.set(null);
  }

  deleteRoutingEdge(edgeId: string): void {
    this.edges.update((edges) => edges.filter((e) => e.id !== edgeId));
    if (this.routingEdgeDetailId() === edgeId) {
      this.routingEdgeDetailId.set(null);
    }
    if (this.selectedEdgeId() === edgeId) {
      this.selectedEdgeId.set(null);
    }
    this.autoSaveSubject.next();
  }

  private maxSortOrderForFrom(fromId: string): number {
    let m = -1;
    for (const e of this.edges()) {
      if (e.fromNodeId !== fromId) continue;
      m = Math.max(m, e.sortOrder ?? 0);
    }
    return m;
  }

  private reorderOutgoingEdges(parentId: string, orderedEdgeIds: string[]): void {
    this.edges.update((edges) =>
      edges.map((e) => {
        if (e.fromNodeId !== parentId) return e;
        const idx = orderedEdgeIds.indexOf(e.id);
        if (idx < 0) return e;
        return { ...e, sortOrder: idx };
      })
    );
    this.autoSaveSubject.next();
  }

  onRoutingEdgeDragStart(ev: DragEvent, edgeId: string): void {
    this.routingDragEdgeId.set(edgeId);
    ev.dataTransfer?.setData('text/plain', edgeId);
    if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'move';
  }

  onRoutingEdgeDragEnd(): void {
    this.routingDragEdgeId.set(null);
  }

  onRoutingEdgeDragOver(ev: DragEvent): void {
    ev.preventDefault();
    if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'move';
  }

  onRoutingEdgeDrop(ev: DragEvent, targetEdgeId: string): void {
    ev.preventDefault();
    const draggedId = this.routingDragEdgeId();
    if (!draggedId || draggedId === targetEdgeId) return;
    const parentId = this.selectedNodeId();
    if (!parentId) return;
    const list = this.outgoingForwardEdges();
    const ids = list.map((x) => x.edge.id);
    const fromIdx = ids.indexOf(draggedId);
    const toIdx = ids.indexOf(targetEdgeId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...ids];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, draggedId);
    this.reorderOutgoingEdges(parentId, next);
    this.routingDragEdgeId.set(null);
  }

  edgeTransitionSelectedValue(edge: WorkflowCanvasEdge): string {
    return edge.transitionType ?? 'ALWAYS';
  }

  onEdgeTransitionTypeChange(edgeId: string, value: unknown): void {
    const t = String(value ?? 'ALWAYS') as WorkflowEdgeTransitionType;
    this.patchEdge(edgeId, {
      transitionType: t,
      ...(t === 'EXPRESSION'
        ? { llmConditionRowId: undefined }
        : { expressionConditionRowId: undefined }),
    });
  }

  expressionOperatorSelectedValue(edge: WorkflowCanvasEdge): string {
    return edge.expressionOperatorDraft ?? WL_ExpressionTypes.EQUALS;
  }

  onExpressionOperatorChange(edgeId: string, value: unknown): void {
    const s = String(value ?? WL_ExpressionTypes.EQUALS);
    this.patchEdge(edgeId, { expressionOperatorDraft: s as WL_ExpressionTypes });
  }

  loadToolsApi(chatbotId: number): void {
    this.loadingTools.set(true);
    this.microServiceHttpService.get<any>('whitelabel', `tools/chatbot/${chatbotId}?includeNodeCount=true&includeNodes=true`, { showToast: false, showLoader: false })
      .pipe(finalize(() => this.loadingTools.set(false)))
      .subscribe({
        next: (res) => {
          this.availableTools.set(Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []);
        }
      });
  }

  isToolAssignedToSelectedNode(toolId: number): boolean {
    const ids = this.selectedNode()?.workflow?.toolIds;
    if (!ids) return false;
    return ids.includes(toolId);
  }

  toggleToolForSelectedNode(toolId: number, ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const checked = input.checked;
    const selCanvasId = this.selectedNodeId();
    if (!selCanvasId) return;

    const node = this.nodes().find(n => n.id === selCanvasId);
    if (!node || node.kind !== 'WORKFLOW' || !node.workflow) return;

    const nodeId = node.workflow.pid;
    if (nodeId == null) {
      input.checked = !checked; // revert UI
      alert('Please save the workflow first to link tools to a new node.');
      return;
    }

    const req$ = checked
      ? this.microServiceHttpService.post<any>('whitelabel', `tools/${toolId}/link/node/${nodeId}`, {}, { showToast: false, successMessage: 'Tool linked successfully', showLoader: false })
      : this.microServiceHttpService.delete<any>('whitelabel', `tools/${toolId}/link/node/${nodeId}`, { showToast: false, successMessage: 'Tool unlinked successfully', showLoader: false });

    req$.subscribe({
      next: () => {
        this.nodes.update(list => list.map(n => {
          if (n.id !== selCanvasId || n.kind !== 'WORKFLOW' || !n.workflow) return n;
          const currentTools = n.workflow.toolIds || [];
          const newTools = checked 
            ? [...currentTools, toolId] 
            : currentTools.filter(id => id !== toolId);
          
          return {
            ...n,
            workflow: { ...n.workflow, toolIds: newTools }
          };
        }));
      },
      error: () => {
        input.checked = !checked; // revert UI on API error
      }
    });
  }

  closePropertiesPanel(): void {
    if (!this.canClosePropertiesPanel()) return;

    // If schedule draft is entirely empty and new (no pid), discard it to allow "walking away"
    const sel = this.selectedNode();
    const draft = sel?.workflow?.scheduleDraft;
    if (draft && !draft.pid) {
      const empty = !draft.name?.trim() && !draft.cronExpr?.trim() && !draft.triggerPrompt?.trim();
      if (empty) {
        this.patchSelectedWorkflow({ scheduleDraft: null });
      }
    }

    this.propertiesPanelOpen.set(false);
    this.routingEdgeDetailId.set(null);
    this.nodeDetailsSub?.unsubscribe();
    this.nodeDetailsSub = null;
    this.propertiesNodeDetailsLoading.set(false);
    this.scheduleTouched.set({});
    
    // Immediate save when closing the panel to ensure settings are persisted
    this.saveWorkflow(undefined, true);
  }

  selectEdge(id: string, ev: Event): void {
    ev.stopPropagation();
    this.pathHighlightNeighborMode.set(false);
    this.selectedEdgeId.set(id);
    this.selectedNodeIds.set([]);
    this.cancelLinkDrag();
  }

  clearCanvasSelection(): void {
    if (this.skipNextCanvasClickClear) {
      this.skipNextCanvasClickClear = false;
      return;
    }

    if (!this.canClosePropertiesPanel()) return;

    this.pathHighlightNeighborMode.set(false);
    this.selectedNodeIds.set([]);
    this.selectedEdgeId.set(null);
    this.cancelLinkDrag();
    const wasOpen = this.propertiesPanelOpen();
    this.propertiesPanelOpen.set(false);
    this.nodeDetailsSub?.unsubscribe();
    this.nodeDetailsSub = null;
    this.propertiesNodeDetailsLoading.set(false);
    this.scheduleTouched.set({});

    if (wasOpen) {
      this.saveWorkflow(undefined, true);
    }
  }

  deleteNode(id: string): void {
    if (isProtectedStartNode(this.getNodeById(id))) return;
    this.nodes.update((list) => list.filter((n) => n.id !== id));
    this.edges.update((e) => e.filter((x) => x.fromNodeId !== id && x.toNodeId !== id));
    this.pathHighlightNeighborMode.set(false);
    this.selectedNodeIds.update((ids) => ids.filter((x) => x !== id));
    if (!this.selectedNode()) {
      const wasOpen = this.propertiesPanelOpen();
      this.propertiesPanelOpen.set(false);
      if (wasOpen) this.saveWorkflow(undefined, true);
    }
    this.autoSaveSubject.next();
  }

  deleteNodes(ids: string[]): void {
    const toRemove = ids.filter((id) => !isProtectedStartNode(this.getNodeById(id)));
    if (toRemove.length === 0) return;
    const removeSet = new Set(toRemove);
    this.nodes.update((list) => list.filter((n) => !removeSet.has(n.id)));
    this.edges.update(
      (e) => e.filter((x) => !removeSet.has(x.fromNodeId) && !removeSet.has(x.toNodeId))
    );
    this.pathHighlightNeighborMode.set(false);
    this.selectedNodeIds.update((s) => s.filter((id) => !removeSet.has(id)));
    if (!this.selectedNode()) {
      const wasOpen = this.propertiesPanelOpen();
      this.propertiesPanelOpen.set(false);
      if (wasOpen) this.saveWorkflow(undefined, true);
    }
    this.autoSaveSubject.next();
  }

  handleKnowledgeBaseUpload(event: { collectionId: number }): void {
    const sel = this.selectedNode();
    if (!sel || !sel.workflow) return;
    
    const selWorkflow = sel.workflow;
    const bid = this.botId() ?? 1;

    // Use workflowNodeId if the particular node has knowledgeBaseId as null
    if (selWorkflow.knowledgeBaseId == null) {
      const payload = {
        workflowNodeId: selWorkflow.pid ?? 0,
        collectionId: event.collectionId
      };

      this.microServiceHttpService.post('whitelabel', 'knowledge-base/collection/link', payload).subscribe({
        next: () => {
          // Update the local state to match the returned API link.
          this.nodes.update(list => list.map(n => {
            if (n.id === sel.id && n.workflow) {
              return {
                ...n,
                workflow: {
                  ...n.workflow,
                  knowledgeBaseId: event.collectionId
                }
              };
            }
            return n;
          }));
          
          // Refresh the documents listing inside the active knowledge base tab
          this.knowledgeBaseComp()?.loadDocuments(bid, selWorkflow.pid ?? null);
        }
      });
    } 
    // Link using only chatbotId when the global knowledgeBaseId is null (passed from parent)
    else if (this.chatbotKnowledgeBaseId() == null) {
      const globalPayload = {
        chatbotId: bid,
        collectionId: event.collectionId
      };
      this.microServiceHttpService.post('whitelabel', 'knowledge-base/collection/link', globalPayload, { showLoader: false, showToast: false }).subscribe({
        next: () => {
          this.knowledgeBaseComp()?.loadDocuments(bid, null);
        }
      });
    } else {
      // Already linked, just refresh the listing
      this.knowledgeBaseComp()?.loadDocuments(bid, selWorkflow.pid ?? null);
    }
  }

  onNodePointerDown(node: WorkflowCanvasNode, ev: PointerEvent): void {
    ev.stopPropagation();
    if (ev.ctrlKey || ev.metaKey) {
      this.toggleNodeSelection(node.id);
      return;
    }
    this.selectSingleNode(node.id);
    const vp = this.viewportRef()?.nativeElement;
    if (!vp) return;
    const r = vp.getBoundingClientRect();
    const lx = ev.clientX - r.left;
    const ly = ev.clientY - r.top;
    const z = this.zoom();
    this.draggingNodeId.set(node.id);
    this.dragOffset = {
      x: lx - (node.x * z + this.panX()),
      y: ly - (node.y * z + this.panY()),
    };
    (ev.target as HTMLElement).setPointerCapture?.(ev.pointerId);
  }

  onOutputHandlePointerDown(nodeId: string, ev: PointerEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.pathHighlightNeighborMode.set(false);
    this.selectedEdgeId.set(null);
    this.selectedNodeIds.set([nodeId]);
    this.draggingLinkSourceId.set(nodeId);
    const w = this.screenToWorld(ev.clientX, ev.clientY);
    this.linkPreviewEndWorld.set(w);
    this.linkHoverTargetId.set(null);
    (ev.currentTarget as HTMLElement).setPointerCapture?.(ev.pointerId);
  }

  private screenToWorld(clientX: number, clientY: number): { x: number; y: number } | null {
    const el = this.viewportRef()?.nativeElement;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const lx = clientX - r.left;
    const ly = clientY - r.top;
    const z = this.zoom();
    return {
      x: (lx - this.panX()) / z,
      y: (ly - this.panY()) / z,
    };
  }

  private updateLinkDrag(ev: PointerEvent): void {
    const w = this.screenToWorld(ev.clientX, ev.clientY);
    if (w) this.linkPreviewEndWorld.set(w);
    const top = document.elementFromPoint(ev.clientX, ev.clientY);
    const h = top?.closest('[data-wf-input-handle]') as HTMLElement | null;
    const tid = h?.dataset['nodeId'] ?? null;
    const src = this.draggingLinkSourceId();
    if (!tid || tid === src) {
      this.linkHoverTargetId.set(null);
    } else {
      this.linkHoverTargetId.set(tid);
    }
  }

  private completeLinkDrag(ev: PointerEvent): void {
    const from = this.draggingLinkSourceId();
    if (!from) return;
    const top = document.elementFromPoint(ev.clientX, ev.clientY);
    const h = top?.closest('[data-wf-input-handle]') as HTMLElement | null;
    const targetId = h?.dataset['nodeId'] ?? null;
    if (targetId && targetId !== from) {
      const exists = this
        .edges()
        .some((e) => e.fromNodeId === from && e.toNodeId === targetId);
      if (!exists) {
        const sortOrder = this.maxSortOrderForFrom(from) + 1;
        this.edges.update((e) => [
          ...e,
          {
            id: uuidv4(),
            fromNodeId: from,
            toNodeId: targetId,
            sortOrder,
            transitionType: 'ALWAYS',
            label: '',
          },
        ]);
        this.autoSaveSubject.next();
      }
    } else if (!targetId) {
      const w = this.screenToWorld(ev.clientX, ev.clientY);
      if (w) {
        const cid = this.effectiveChatbotId();
        const nx = Math.min(
          CANVAS_WORLD_W - NODE_W - 8,
          Math.max(8, Math.round(w.x))
        );
        const ny = Math.min(
          CANVAS_WORLD_H - NODE_H - 8,
          Math.max(8, Math.round(w.y - CONNECTOR_Y))
        );
        const node = newAgentCanvasNode(cid, nx, ny);
        this.nodes.update((l) => [...l, node]);
        const sortOrder = this.maxSortOrderForFrom(from) + 1;
        this.edges.update((e) => [
          ...e,
          {
            id: uuidv4(),
            fromNodeId: from,
            toNodeId: node.id,
            sortOrder,
            transitionType: 'ALWAYS',
            label: '',
          },
        ]);
        this.pathHighlightNeighborMode.set(false);
        this.selectedNodeIds.set([node.id]);
        this.autoSaveSubject.next();
      }
    }
    this.cancelLinkDrag();
  }

  copyWorkflowNode(nodeId: string, ev: Event): void {
    ev.stopPropagation();
    ev.preventDefault();
    const src = this.getNodeById(nodeId);
    if (!src || src.kind !== 'WORKFLOW' || !src.workflow) return;
    try {
      const copy = cloneAgentCanvasNode(src, src.x + 28, src.y + 28);
      this.nodes.update((l) => [...l, copy]);
      this.pathHighlightNeighborMode.set(false);
      this.selectedNodeIds.set([copy.id]);
      this.selectedEdgeId.set(null);
      this.autoSaveSubject.next();
    } catch {
      /* ignore */
    }
  }

  cancelLinkDrag(): void {
    this.draggingLinkSourceId.set(null);
    this.linkPreviewEndWorld.set(null);
    this.linkHoverTargetId.set(null);
  }

  linkPreviewPath(): string {
    const srcId = this.draggingLinkSourceId();
    if (!srcId) return '';
    const from = this.getNodeById(srcId);
    if (!from) return '';
    
    const hover = this.linkHoverTargetId();
    let targetX: number;
    let targetY: number;
    let p2: { x: number, y: number, dirX: number, dirY: number };
    
    if (hover) {
      const to = this.getNodeById(hover);
      if (!to) return '';
      const tCenter = this.getRectCenter(to);
      const fCenter = this.getRectCenter(from);
      targetX = tCenter.x;
      targetY = tCenter.y;
      p2 = this.getNodeConnectionLayout(to, fCenter.x, fCenter.y);
    } else {
      const end = this.linkPreviewEndWorld();
      if (!end) return '';
      targetX = end.x;
      targetY = end.y;
      p2 = { x: targetX, y: targetY, dirX: 0, dirY: 0 };
    }
    
    const p1 = this.getNodeConnectionLayout(from, targetX, targetY);
    
    const dist = Math.max(48, Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)) * 0.4);
    const c1x = p1.x + p1.dirX * dist;
    const c1y = p1.y + p1.dirY * dist;
    const c2x = p2.x + p2.dirX * dist;
    const c2y = p2.y + p2.dirY * dist;
    
    return `M ${p1.x} ${p1.y} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }

  patchSelectedWorkflow(patch: Partial<WorkflowNodeDraft>): void {
    const id = this.selectedNodeId();
    if (!id) return;
    this.nodes.update((list) =>
      list.map((n) => {
        if (n.id !== id || !n.workflow) return n;
        return { ...n, workflow: { ...n.workflow, ...patch } };
      })
    );
  }

  patchNodeSchedule(patch: Partial<NodeScheduleDraft>): void {
    const id = this.selectedNodeId();
    if (!id) return;
    this.nodes.update((list) =>
      list.map((n) => {
        if (n.id !== id || !n.workflow) return n;
        const existing = n.workflow.scheduleDraft ?? {
          pid: null,
          name: '',
          triggerPrompt: '',
          cronExpr: '',
          timezone: 'UTC',
          isActive: true,
          maxRetries: 0,
          retryIntervalMins: 5,
          upstreamWorkflowIds: [],
        };
        return { ...n, workflow: { ...n.workflow, scheduleDraft: { ...existing, ...patch } } };
      })
    );
  }

  ensureScheduleDraft(): void {
    const sel = this.selectedNode();
    if (sel && sel.workflow && !sel.workflow.scheduleDraft) {
      this.patchNodeSchedule({});
    }
  }

  saveNodeSchedule(): void {
    const sel = this.selectedNode();
    const draft = sel?.workflow?.scheduleDraft;
    const nodePid = sel?.workflow?.pid;
    const botId = this.botId();
    if (!draft || !nodePid || !botId) {
      toast.error('Save the workflow graph first before configuring a schedule.');
      return;
    }
    if (!draft.cronExpr.trim()) {
      toast.error('Cron expression is required.');
      return;
    }
    if (!draft.triggerPrompt.trim()) {
      toast.error('Trigger prompt is required.');
      return;
    }

    // Convert ids to numbers and filter valid positive integers
    const upstreamIds = (draft.upstreamWorkflowIds || [])
      .map((id) => Number(id))
      .filter((id) => !isNaN(id) && id > 0);
    if (upstreamIds.length === 0) {
      toast.error('Please select at least one upstream agent to include context from.');
      return;
    }
    const finalDraft = { ...draft, upstreamWorkflowIds: upstreamIds };

    this.scheduleAgentService.upsertNodeSchedule(botId, nodePid, finalDraft).subscribe({
      next: (res: any) => {
        const saved = res?.data;
        if (saved?.pid) {
          this.patchNodeSchedule({ pid: saved.pid });
        }
        toast.success('Schedule saved');
      },
      error: () => toast.error('Failed to save schedule'),
    });
  }

  patchStartMeta(patch: Partial<{ name: string; systemPrompt: string | null }>): void {
    this.nodes.update((list) =>
      list.map((n) => {
        if (n.kind !== 'START' || !n.startMeta) return n;
        const startMeta = { ...n.startMeta, ...patch };
        const spUi =
          startMeta.systemPrompt === null || startMeta.systemPrompt === undefined
            ? ''
            : startMeta.systemPrompt;
        const workflow = n.workflow
          ? { ...n.workflow, name: startMeta.name, systemPrompt: spUi }
          : n.workflow;
        return { ...n, startMeta, workflow };
      })
    );
  }

  getNodeById(id: string): WorkflowCanvasNode | undefined {
    return this.nodes().find((n) => n.id === id);
  }

  getRectCenter(node: WorkflowCanvasNode): { x: number; y: number } {
    return { x: node.x + this.NODE_W / 2, y: node.y + this.NODE_H / 2 };
  }

  getRectIntersection(node: WorkflowCanvasNode, targetX: number, targetY: number): { x: number; y: number; dirX: number; dirY: number } {
    const rectX = node.x;
    const rectY = node.y;
    const rectW = this.NODE_W;
    const rectH = this.NODE_H;
    
    const cx = rectX + rectW / 2;
    const cy = rectY + rectH / 2;
    const dx = targetX - cx;
    const dy = targetY - cy;
    
    if (dx === 0 && dy === 0) return { x: cx, y: cy, dirX: 1, dirY: 0 };
    
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    
    // Choose the closest mid-side endpoint
    if (absDx * rectH > absDy * rectW) {
      // Horizontal side (Left or Right)
      if (dx > 0) return { x: rectX + rectW, y: cy, dirX: 1, dirY: 0 };
      else return { x: rectX, y: cy, dirX: -1, dirY: 0 };
    } else {
      // Vertical side (Top or Bottom)
      if (dy > 0) return { x: cx, y: rectY + rectH, dirX: 0, dirY: 1 };
      else return { x: cx, y: rectY, dirX: 0, dirY: -1 };
    }
  }

  getNodeInputHandles(node: WorkflowCanvasNode): { style: any }[] {
    const sides = new Set<string>();

    const hoverId = this.linkHoverTargetId();
    const dragSourceId = this.draggingLinkSourceId();
    if (hoverId === node.id && dragSourceId) {
      const source = this.getNodeById(dragSourceId);
      if (source) {
        const sCenter = this.getRectCenter(source);
        const p = this.getRectIntersection(node, sCenter.x, sCenter.y);
        sides.add(`${p.dirX},${p.dirY}`);
      }
    }

    const edges = this.edges();
    for (const edge of edges) {
      if (edge.toNodeId === node.id) {
        const source = this.getNodeById(edge.fromNodeId);
        if (source) {
          const sCenter = this.getRectCenter(source);
          const p = this.getRectIntersection(node, sCenter.x, sCenter.y);
          sides.add(`${p.dirX},${p.dirY}`);
        }
      }
    }

    if (sides.size === 0) {
      sides.add('-1,0');
    }

    return Array.from(sides).map((key) => {
      const parts = key.split(',');
      const dx = Number(parts[0]);
      const dy = Number(parts[1]);
      if (dx === -1) return { style: { left: '-6px', top: '50%', transform: 'translateY(-50%)' } };
      if (dx === 1) return { style: { right: '-6px', top: '50%', transform: 'translateY(-50%)' } };
      if (dy === -1) return { style: { left: '50%', top: '-6px', transform: 'translateX(-50%)' } };
      if (dy === 1) return { style: { left: '50%', bottom: '-6px', transform: 'translateX(-50%)' } };
      return { style: {} };
    });
  }

  getNodeConnectionLayout(a: WorkflowCanvasNode, targetX: number, targetY: number): { x: number; y: number; dirX: number; dirY: number } {
     return this.getRectIntersection(a, targetX, targetY);
  }

  edgePath(edge: WorkflowCanvasEdge): string {
    const a = this.getNodeById(edge.fromNodeId);
    const b = this.getNodeById(edge.toNodeId);
    if (!a || !b) return '';
    const bCenter = this.getRectCenter(b);
    const aCenter = this.getRectCenter(a);
    const p1 = this.getNodeConnectionLayout(a, bCenter.x, bCenter.y);
    const p2 = this.getNodeConnectionLayout(b, aCenter.x, aCenter.y);
    
    const dist = Math.max(48, Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)) * 0.4);
    const c1x = p1.x + p1.dirX * dist;
    const c1y = p1.y + p1.dirY * dist;
    const c2x = p2.x + p2.dirX * dist;
    const c2y = p2.y + p2.dirY * dist;
    
    return `M ${p1.x} ${p1.y} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }

  /** Resets only canvas pan/zoom (not node positions). */
  resetView(ev?: Event): void {
    ev?.stopPropagation();
    this.panX.set(0);
    this.panY.set(0);
    this.zoom.set(DEFAULT_ZOOM);
  }

  zoomIn(ev?: Event): void {
    ev?.stopPropagation();
    this.zoom.update((z) => Math.min(1.55, z + 0.08));
  }

  zoomOut(ev?: Event): void {
    ev?.stopPropagation();
    this.zoom.update((z) => Math.max(0.35, z - 0.08));
  }

  toggleFullscreen(ev?: Event): void {
    ev?.stopPropagation();
    this.setFullscreen(!this.fullscreen());
  }

  setFullscreen(on: boolean): void {
    this.fullscreen.set(on);
    this.document.body.style.overflow = on ? 'hidden' : '';
  }

  ngOnDestroy(): void {
    this.document.body.style.overflow = '';
    this.autoSaveSub?.unsubscribe();
    // Cleanup panel count if destroyed while open
    if (this.propertiesPanelOpen()) {
      this.commonService.updateBodyPanelCount(-1);
    }
  }

  readonly NODE_W = NODE_W;
  readonly NODE_H = NODE_H;
  readonly NODE_CHROME_OFFSET_PX = NODE_CHROME_OFFSET_PX;
}
